<template>
    <div class="private-products container _dgrid _gap-32-8 _grid-cols-12-1">
        <div class="_grid-col-span-7-1">
            <div class="private-products__image-wrapper">
                <transition
                    name="private-banking__transition_fade"
                >
                    <div
                        :key="`image-${activeItem}`"
                        class="private-products__image"
                        :style="{
                            backgroundImage,
                            '--image-anim-delay': animationDelay ? `${animationDelay / 1000}s` : '0s',
                        }"
                    />
                </transition>
            </div>
            <div
                v-if="showArrows"
                class="_dflex _justify-content-center _mobile-hidden"
            >
                <button
                    class="private-products__button_arrow _mr-16"
                    type="button"
                    @click="onPrev"
                >
                    <div
                        class="ico ico_size_16 ico-arrow-corner-left-bold  _mr-auto _ml-auto"
                        :class="{
                            [activeItem === 0 ? 'ico_color_grey-517' : 'ico_color_white']: true,
                        }"
                    />
                </button>
                <button
                    class="private-products__button_arrow"
                    type="button"
                    @click="onNext"
                >
                    <div
                        class="ico ico_size_16 ico-arrow-corner-right-bold _mr-auto _ml-auto"
                        :class="{
                            [activeItem === products.length - 1 ? 'ico_color_grey-517' : 'ico_color_white']: true,
                        }"
                    />
                </button>
            </div>
        </div>
        <div class="_grid-col-span-5-1 _pt-64-0  _position-relative _dflex _flexdir-column _flexdir-mob-column-re">
            <div
                v-if="switcher === 'buttons'"
                class="_dflex _mb-32"
            >
                <div
                    v-for="(product, i) in products"
                    :key="i"
                    class="private-products__switcher-btn"
                    :class="{'private-products__switcher-btn_active': activeItem === i }"
                    @click="activeItem = i"
                >{{ product.title }}</div>
            </div>
            <div
                ref="content-wrapper"
                class="private-products__content-wrapper"
            >
                <transition
                    name="fade"
                    appear
                    @enter="onEnter"
                    @appear-enter="onEnter"
                >
                    <component
                        ref="content"
                        :is="isMobile ? 'UiShowMore' : 'div'"
                        :height="184"
                        :key="`content-${activeItem}`"
                        class="private-products__content"
                        @toggle-progress="setWrapperHeight"
                    >
                        <UiWrapDefaultSlotItem
                            :slots="getContentSlots()"
                        />
                        <template #activator="{ toggle, heightIsGreatherScroll }">
                            <button
                                v-if="heightIsGreatherScroll"
                                type="button"
                                class="button button_black _mt-24"
                                button-text="Показать еще"
                                @click="toggle"
                            >
                                <span class="_mr-8 ico ico-arrow-corner-down-bold ico_color_white ico_size_16"></span>
                                Показать еще
                            </button>
                        </template>
                    </component>
                </transition>
            </div>
            <PrivateRuler
                v-if="switcher === 'ruler'"
                v-model="activeItem"
                :products="productTitles"
                class="_w-100 _mb-0-32"
            />
        </div>
    </div>
</template>
<script>
import UiWrapDefaultSlotItem from '../ui/UiWrapDefaultSlotItem.vue';
import UiShowMore from '../ui/UiShowMore.vue';
import { computed, ref } from 'vue';
import PrivateRuler from './PrivateRuler.vue';
import { useStore } from '../../store';

export default {
    name: 'PrivateProducts',
    components: {
        UiWrapDefaultSlotItem,
        UiShowMore,
        PrivateRuler,
    },
    props: {
        /**
         * Продукты
         * @type {Product[]}
         * @property {string} Product.image - урл картинки в левом блоке
         * @property {string} Product.title - название на "счетчике"
         */
        products: {
            type: Array,
            default: () => [],
        },
        switcher: {
            type: String,
            default: 'ruler',
            validator: (val) => ['ruler', 'buttons'].includes(val),
        },
        showArrows: Boolean,
        /**
         * image animation delay (for async start animation), in ms
         */
        animationDelay: Number,
    },
    setup(props, { slots }) {
        const store = useStore();
        const isMobile = computed(() => store.state.app.isMobile);
        const activeItem = ref(0);

        const getContentSlots = () => {
            const activeContent = slots.default().filter((slot) => !!slot.tag)[activeItem.value];
            return activeContent ? [activeContent] : [];
        };

        return {
            isMobile,
            activeItem,
            getContentSlots,
        };
    },
    data() {
        return {
            duration: 600,
        };
    },
    computed: {
        productTitles() {
            return this.products.map(({ title }) => title);
        },
        backgroundImage() {
            return `url(${this.products[this.activeItem]?.image})`;
        },
    },
    methods: {
        onNext() {
            if (this.activeItem >= this.products.length - 1) return;

            this.activeItem++;
        },
        onPrev() {
            if (this.activeItem === 0) return;

            this.activeItem--;
        },
        async onEnter() {
            await this.$nextTick();
            this.setWrapperHeight();
        },
        setWrapperHeight() {
            const contentEl = this.$refs.content instanceof Element ? this.$refs.content : this.$refs.content.$el;
            const h = contentEl.scrollHeight;
            this.$refs['content-wrapper'].style.height = `${h}px`;
        }
    },
};
</script>
<style lang="scss">
@import '../../assets/scss/base/variables';
@import '../../assets/scss/base/mixins';
@import '../../assets/scss/base/functions';

.private-products {
    @include fluid('min-height', 711px, 222px);

    &__item {
    }

    &__item-title {
        font-weight: 500;
        @include fluid('font-size', 44px);
        @include fluid('line-height', 56px);
        @include fluid('margin-bottom', 24px);

        @include mobile {
            display: none;
        }
    }

    &__item-content {
        @include p1;

        & ul li {
            background: url('/local/templates/redesign/src/assets/img/private/products/product-list.webp') no-repeat;
            @include fluid('background-size', 9px, 7px);
            @include fluid('background-position-x', 0px, 12px);
            @include fluid('background-position-y', 6px, 4px);
            list-style: none;
            @include fluid('padding-left', 22px, 28px);
            @include fluid('margin-bottom', 8px);
        }
    }

    &__image-wrapper {
        width: 100%;
        position: relative;
        @include fluid('height', 571px, 222px);
    }

    &__image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;

        animation: 2s infinite alternate imageanim ease-in-out;
        animation-delay: var(--image-anim-delay, 0s);
        @include fluid(--image-max-scale, 24px, 6px);

        @keyframes imageanim {
            from {
                transform: translateY(0);
            }

            to {
                transform: translateY(var(--image-max-scale));
            }
        }
    }

    &__button_arrow {
        border-radius: 50%;
        background-color: $grey-868;
        cursor: pointer;
        @include fluid('width', 32px);
        @include fluid('height', 32px);
    }

    &__content-wrapper {
        position: relative;
        @include fluid('margin-bottom', 72px, 0px);
    }

    &__content {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
    }

    &__switcher-btn {
        background-color: $gray-private;
        box-sizing: border-box;
        cursor: pointer;
        @include fluid('padding-top', 6px);
        @include fluid('padding-bottom', 6px);
        @include fluid('padding-right', 16px);
        @include fluid('padding-left', 16px);
        @include fluid('border-radius', 8px);
        @include fluid('margin-right', 16px);
        transition: .2s;

        &:hover {
            background-color: $gray-premium-content;
            transition: .2s;
        }

        &_active {
            background-color: $black-private;
            box-shadow: 0 0 0 2px #FFA360;
            transition: .2s;
        }
    }
}
</style>
